"use client"

import { useState, useEffect, useRef, memo } from "react"
import { useVirtualizer } from "@tanstack/react-virtual"
import { AlertTriangle, ChevronDown, Circle, Info, MapPin, Search, Zap } from "lucide-react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Tooltip, TooltipContent, TooltipPortal, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import type { AtsLob, AtsClientSyncSetting } from "@/types/ats/ats-sync-settings"
import { ApplyToAllModal } from "@/components/ats/apply-to-all-modal"
import type { AtsSyncSettingsFilters } from "@/hooks/ats/use-ats-sync-settings-filters"
import { getClientRowState } from "@/lib/ats/get-client-row-state"
import { Z_INDEX } from "@/lib/z-index"
import { useDebounce } from "@/hooks/use-debounce"
import { useTranslation } from "@/hooks/use-translation"
import { CustomFilterIcon } from "@/components/custom-filter-icon"
import { WindowRefService } from "@/lib/window-ref-service"

// The client id is the FBO org id. Open the org's FBO portal page in a new tab.
//
// The portal base URL is environment-specific and lives in the backend "ClientLink"
// action configuration (ActionType external_link) — its Link is a full portal
// template, e.g. "https://uat.flexbackoffice.com/next/organization/${Client.FboId}/details".
// We deliberately do NOT derive it from env.apiUrl: in deployed environments the API
// sits on a different host (e.g. uat-api.flexbackoffice.com) than the portal, which is
// why the link was previously wrong. The parent (ats-sub-tab-content) resolves the
// ClientLink action from the WHC configuration and passes its Link here as linkTemplate.
//
// Fallback (linkTemplate absent — e.g. the legacy bespoke tab, or a config that doesn't
// carry the ClientLink action yet): use the current window origin, which at least avoids
// the API host. (The host NAVIGATE bridge isn't wired for this route, so we open the tab
// directly via WindowRefService.)
function openOrgInFbo(orgId: number, linkTemplate?: string) {
  let url: string
  if (linkTemplate && linkTemplate.trim()) {
    // Substitute the placeholder (e.g. ${Client.FboId}) with the org id.
    url = linkTemplate.replace(/\$\{[^}]+\}/g, String(orgId))
  } else {
    const origin = typeof window !== "undefined" ? window.location.origin : ""
    url = `${origin}/next/organization/${orgId}/details`
  }
  WindowRefService.openUrl(url)
}

function SyncSettingsSearchInput({ onSearchTextChange }: { onSearchTextChange: (value: string) => void }) {
  const [inputValue, setInputValue] = useState("")
  const { t } = useTranslation()
  const debouncedInputValue = useDebounce(inputValue, 300)

  useEffect(() => {
    onSearchTextChange(debouncedInputValue)
  }, [debouncedInputValue, onSearchTextChange])

  return (
    <div className="relative flex-1">
      <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
      <Input
        aria-label={t('atsSyncSearchLabel')}
        placeholder={t('atsSyncSearchPlaceholder')}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        className="pl-8 text-sm"
      />
    </div>
  )
}

const CLIENT_COL_WIDTH = 280
const ALL_COL_WIDTH = 90

interface SyncSettingsRowProps {
  client: AtsClientSyncSetting
  lobs: AtsLob[]
  enabledLobIds: number[]
  duplicateAtsClientIds: Set<string>
  addressLine: string | null
  isModified: boolean
  tooltipContainer: HTMLElement | null
  orgDetailsLinkTemplate?: string
  onToggleLob: (clientId: number, lobId: number) => void
  onToggleAll: (clientId: number) => void
}

const SyncSettingsRow = memo(function SyncSettingsRow({
  client,
  lobs,
  enabledLobIds,
  duplicateAtsClientIds,
  addressLine,
  isModified,
  tooltipContainer,
  orgDetailsLinkTemplate,
  onToggleLob,
  onToggleAll,
}: SyncSettingsRowProps) {
  const { t } = useTranslation()
  const { enabled, warning } = getClientRowState(client.atsClients, enabledLobIds, duplicateAtsClientIds)

  const allState: true | false | "indeterminate" =
    lobs.length > 0 && lobs.every(l => enabledLobIds.includes(l.id))
      ? true
      : enabledLobIds.length === 0
        ? false
        : "indeterminate"

  const dotClass = !enabled
    ? "fill-gray-300 stroke-gray-300"
    : warning !== null
      ? "fill-amber-400 stroke-amber-400"
      : "fill-green-500 stroke-green-500"

  return (
    <tr className="border-b border-gray-50 hover:bg-gray-50/50">
      <td className="py-2 pl-6 pr-4" style={{ background: "#fff", position: "sticky", left: 0, zIndex: Z_INDEX.FIXED_CELL + 1, width: CLIENT_COL_WIDTH, minWidth: CLIENT_COL_WIDTH, maxWidth: CLIENT_COL_WIDTH, borderRight: "1px solid #ECECEC" }}>
        {isModified && <div className="absolute inset-y-0 left-0 w-[3px] bg-blue-500" />}
        <div className="flex items-start gap-2">
          <Circle size={6} className={`mt-[7px] shrink-0 ${dotClass}`} />
          <div className="min-w-0">
            <button
              type="button"
              onClick={() => openOrgInFbo(client.id, orgDetailsLinkTemplate)}
              title={client.name || `ID ${client.id} (No name)`}
              className="block max-w-full truncate text-left text-blue-600 hover:underline focus:outline-none focus:underline"
            >
              {client.name || <span className="italic">ID {client.id} — No name</span>}
            </button>
            {client.atsClients.length > 0 ? (() => {
              const dupeIds = client.atsClients.filter(c => duplicateAtsClientIds.has(c.id))
              return (
                <div className="flex items-center gap-1 mt-0.5">
                  <div
                    className="text-xs text-gray-400 truncate min-w-0"
                    title={client.atsClients.map(c => c.name ? `ATS: ${c.id} · ${c.name}` : `ATS: ${c.id}`).join(" / ")}
                  >
                    {client.atsClients.map((c, i) => (
                      <span key={c.id}>
                        {i > 0 && <span className="text-gray-300 mx-0.5">/</span>}
                        ATS: {c.id}{c.name ? ` · ${c.name}` : ""}
                      </span>
                    ))}
                  </div>
                  {warning === "duplicate" && (
                    <Tooltip>
                      <TooltipTrigger className="shrink-0 inline-flex cursor-help p-0 bg-transparent border-none">
                        <AlertTriangle size={11} className="text-amber-400" />
                      </TooltipTrigger>
                      <TooltipPortal container={tooltipContainer}>
                        <TooltipContent side="top" className="max-w-xs">
                          {dupeIds.length === 1
                            ? t('atsSyncDuplicateSingleTooltip', { id: String(dupeIds[0]!.id), name: dupeIds[0]!.name ? ` (${dupeIds[0]!.name})` : "" })
                            : t('atsSyncDuplicateMultipleTooltip', { ids: dupeIds.map(c => c.name ? `${c.id} (${c.name})` : String(c.id)).join(", ") })
                          }
                        </TooltipContent>
                      </TooltipPortal>
                    </Tooltip>
                  )}
                </div>
              )
            })() : (
              <div className="flex items-center gap-1 mt-0.5">
                <span className="text-xs text-gray-400">ATS: —</span>
                {warning === "no-mapping" && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <AlertTriangle size={11} className="shrink-0 text-amber-400 cursor-help" />
                    </TooltipTrigger>
                    <TooltipPortal container={tooltipContainer}>
                      <TooltipContent side="top" className="max-w-xs">
                        {t('atsSyncNoMappingTooltip')}
                      </TooltipContent>
                    </TooltipPortal>
                  </Tooltip>
                )}
              </div>
            )}
            <div className="flex items-center gap-1 mt-0.5">
              <MapPin size={10} className="shrink-0 text-gray-400" />
              <span className="text-xs text-gray-400 truncate" title={addressLine ?? undefined}>{addressLine ?? "—"}</span>
            </div>
          </div>
        </div>
      </td>
      <td className="py-2.5 px-2 text-center" style={{ background: "#fff", position: "sticky", left: CLIENT_COL_WIDTH, zIndex: Z_INDEX.FIXED_CELL, minWidth: ALL_COL_WIDTH, borderRight: "1px solid #ECECEC" }}>
        <Checkbox
          checked={allState}
          aria-label={`${client.name || `Client ${client.id}`}: all lines of business`}
          onCheckedChange={() => onToggleAll(client.id)}
        />
      </td>
      {lobs.map((lob) => (
        <td key={lob.id} className="py-2.5 px-2 text-center">
          <Checkbox
            checked={enabledLobIds.includes(lob.id)}
            aria-label={`${client.name || `Client ${client.id}`}: ${lob.name}`}
            onCheckedChange={() => onToggleLob(client.id, lob.id)}
          />
        </td>
      ))}
    </tr>
  )
})

interface DefaultSyncSettingsRowProps {
  client: AtsClientSyncSetting
  lobs: AtsLob[]
  enabledLobIds: number[]
  allState: true | false | "indeterminate"
  tooltipContainer: HTMLElement | null
  onToggleLob: (clientId: number, lobId: number) => void
  onToggleAll: (clientId: number) => void
}

/**
 * Pinned top row for the synthetic "Unmapped clients (default)" config (server id 0). Unlike a real
 * client it has no ATS-id or location lines and gets a grey background + info tooltip so it reads as
 * a global rule. Its enabled LOBs gate auto-create for placements with no FBO-mapped client.
 */
const DefaultSyncSettingsRow = memo(function DefaultSyncSettingsRow({
  client,
  lobs,
  enabledLobIds,
  allState,
  tooltipContainer,
  onToggleLob,
  onToggleAll,
}: DefaultSyncSettingsRowProps) {
  const { t } = useTranslation()
  const greyBg = "#F4F4F6"

  // "Active" = at least one LOB enabled (this default rule will auto-create unmapped placements).
  // No ATS-mapping/duplicate concept applies to the synthetic default row, so it's a simple
  // green/grey toggle rather than the regular row's three-state dot.
  const isActive = enabledLobIds.length > 0
  const dotClass = isActive ? "fill-green-500 stroke-green-500" : "fill-gray-300 stroke-gray-300"

  return (
    <tr className="border-b border-gray-200">
      <td className="py-2 pl-6 pr-4" style={{ background: greyBg, position: "sticky", left: 0, zIndex: Z_INDEX.FIXED_CELL + 1, width: CLIENT_COL_WIDTH, minWidth: CLIENT_COL_WIDTH, maxWidth: CLIENT_COL_WIDTH, borderRight: "1px solid #ECECEC" }}>
        <div className="flex items-start gap-2">
          <Circle size={6} className={`mt-[7px] shrink-0 ${dotClass}`} />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-gray-800 font-semibold">{t('atsSyncUnmappedClientsLabel')}</span>
              <Tooltip>
                <TooltipTrigger className="shrink-0 inline-flex cursor-help p-0 bg-transparent border-none">
                  <Info size={13} className="text-gray-400" />
                </TooltipTrigger>
                <TooltipPortal container={tooltipContainer}>
                  <TooltipContent side="top" className="max-w-xs">
                    {t('atsSyncUnmappedClientsTooltip')}
                  </TooltipContent>
                </TooltipPortal>
              </Tooltip>
            </div>
            <div className="text-xs text-gray-400 truncate mt-0.5" title={t('atsSyncUnmappedClientsSubtitle')}>
              {t('atsSyncUnmappedClientsSubtitle')}
            </div>
          </div>
        </div>
      </td>
      <td className="py-2.5 px-2 text-center" style={{ background: greyBg, position: "sticky", left: CLIENT_COL_WIDTH, zIndex: Z_INDEX.FIXED_CELL, minWidth: ALL_COL_WIDTH, borderRight: "1px solid #ECECEC" }}>
        <Checkbox
          checked={allState}
          aria-label={`${t('atsSyncUnmappedClientsLabel')}: all lines of business`}
          onCheckedChange={() => onToggleAll(client.id)}
        />
      </td>
      {lobs.map((lob) => (
        <td key={lob.id} className="py-2.5 px-2 text-center" style={{ background: greyBg }}>
          <Checkbox
            checked={enabledLobIds.includes(lob.id)}
            aria-label={`${t('atsSyncUnmappedClientsLabel')}: ${lob.name}`}
            onCheckedChange={() => onToggleLob(client.id, lob.id)}
          />
        </td>
      ))}
    </tr>
  )
})

function ComboboxSelect({
  value,
  onChange,
  options,
  anyLabel,
  searchPlaceholder,
  noResultsLabel,
  disabled = false,
  portalContainer,
}: {
  value: number | null
  onChange: (value: number | null) => void
  options: Array<{ id: number; text: string }>
  anyLabel: string
  searchPlaceholder: string
  noResultsLabel: string
  disabled?: boolean
  portalContainer: HTMLElement | null
}) {
  const [open, setOpen] = useState(false)
  const selected = value !== null ? options.find(o => o.id === value) : null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="flex h-8 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-1.5 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={disabled}
        >
          <span className="truncate">{selected ? selected.text : anyLabel}</span>
          <ChevronDown size={14} className="shrink-0 opacity-50 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent portalContainer={portalContainer} className="p-0 w-56" align="start" sideOffset={4}>
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{noResultsLabel}</CommandEmpty>
            <CommandGroup>
              <CommandItem value={anyLabel} onSelect={() => { onChange(null); setOpen(false) }}>
                {anyLabel}
              </CommandItem>
              {options.map(o => (
                <CommandItem key={o.id} value={o.text} onSelect={() => { onChange(o.id); setOpen(false) }}>
                  {o.text}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

function FiltersPopover({ filters, portalContainer }: { filters: AtsSyncSettingsFilters; portalContainer: HTMLElement | null }) {
  const [container, setContainer] = useState<HTMLDivElement | null>(null)
  const { t } = useTranslation()
  const [cityInput, setCityInput] = useState(filters.city)
  const debouncedCity = useDebounce(cityInput, 300)

  useEffect(() => {
    filters.setCity(debouncedCity)
  }, [debouncedCity, filters.setCity])

  useEffect(() => {
    setCityInput(filters.city)
  }, [filters.city])

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="elliptical-outline" className="relative shrink-0 flex items-center gap-1.5">
          <CustomFilterIcon size={14} />
          {t('atsSyncFilters')}
          {filters.activeCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center w-4 h-4 rounded-full bg-blue-600 text-white text-[10px] font-semibold leading-none">
              {filters.activeCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-0 flex flex-col gap-0" highZIndex portalContainer={portalContainer}>
      <div ref={setContainer} className="p-4 flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-gray-700">{t('atsSyncStatus')}</span>
          <div className="grid grid-cols-3 gap-1">
            {(["all", "enabled", "disabled"] as const).map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => filters.setStatus(opt)}
                className={`h-8 rounded-md border text-xs transition-colors ${
                  filters.status === opt
                    ? "border-blue-500 bg-blue-50 text-blue-700"
                    : "border-input text-gray-600 hover:bg-gray-50"
                }`}
              >
                {t(opt === "all" ? "atsSyncStatusAll" : opt === "enabled" ? "atsSyncStatusEnabled" : "atsSyncStatusDisabled")}
              </button>
            ))}
          </div>
        </div>

        <label className="flex items-center justify-between text-sm font-medium text-gray-700 cursor-pointer">
          {t('atsSyncWarningsOnly')}
          <Switch className="data-[state=checked]:bg-blue-600" checked={filters.warningsOnly} onCheckedChange={filters.setWarningsOnly} />
        </label>

        <label className="flex items-center justify-between text-sm font-medium text-gray-700 cursor-pointer">
          {t('atsSyncUnmappedOnly')}
          <Switch className="data-[state=checked]:bg-blue-600" checked={filters.unmappedOnly} onCheckedChange={filters.setUnmappedOnly} />
        </label>

        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-gray-700">{t('atsSyncCountry')}</span>
          <ComboboxSelect
            value={filters.countryId}
            onChange={filters.setCountry}
            options={filters.countries}
            anyLabel={t('atsSyncAny')}
            searchPlaceholder={t('atsSyncSearchCountry')}
            noResultsLabel={t('atsSyncNoResults')}
            portalContainer={container}
          />
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-gray-700">{t('atsSyncProvinceState')}</span>
          <ComboboxSelect
            value={filters.subdivisionId}
            onChange={filters.setSubdivisionId}
            options={filters.subdivisions}
            anyLabel={filters.countryId === null ? t('atsSyncSelectCountryFirst') : t('atsSyncAny')}
            searchPlaceholder={t('atsSyncSearchSubdivision')}
            noResultsLabel={t('atsSyncNoResults')}
            disabled={filters.countryId === null}
            portalContainer={container}
          />
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-gray-700">{t('atsSyncCity')}</span>
          <Input
            value={cityInput}
            onChange={(e) => setCityInput(e.target.value)}
            placeholder={t('atsSyncAny')}
            className="h-8 text-sm"
          />
        </div>

        <div className="flex items-center justify-center pt-2 border-t border-gray-100">
          <button
            className="text-sm font-medium text-red-600 hover:text-red-700 disabled:text-gray-300 disabled:hover:text-gray-300"
            onClick={filters.clear}
            disabled={filters.activeCount === 0}
          >
            {t('clearAll')}
          </button>
        </div>
      </div>
      </PopoverContent>
    </Popover>
  )
}

interface SyncSettingsTableProps {
  filteredClients: AtsClientSyncSetting[]
  lobs: AtsLob[]
  draft: Record<number, number[]>
  duplicateAtsClientIds: Set<string>
  changedClientIds: Set<number>
  tooltipContainer: HTMLElement | null
  defaultClient: AtsClientSyncSetting | null
  getAllCheckboxState: (clientId: number) => true | false | "indeterminate"
  getAddressLine: (client: AtsClientSyncSetting) => string | null
  orgDetailsLinkTemplate?: string
  onToggleLob: (clientId: number, lobId: number) => void
  onToggleAll: (clientId: number) => void
}

function SyncSettingsTable({
  filteredClients,
  lobs,
  draft,
  duplicateAtsClientIds,
  changedClientIds,
  tooltipContainer,
  defaultClient,
  getAllCheckboxState,
  getAddressLine,
  orgDetailsLinkTemplate,
  onToggleLob,
  onToggleAll,
}: SyncSettingsTableProps) {
  const { t } = useTranslation()
  const scrollRef = useRef<HTMLDivElement>(null)

  const rowVirtualizer = useVirtualizer({
    count: filteredClients.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 57,
    overscan: 5,
  })

  const virtualItems = rowVirtualizer.getVirtualItems()
  const totalSize = rowVirtualizer.getTotalSize()
  const paddingTop = virtualItems.length > 0 ? virtualItems[0]!.start : 0
  const paddingBottom = virtualItems.length > 0 ? totalSize - virtualItems[virtualItems.length - 1]!.end : 0

  return (
    <div ref={scrollRef} className="flex-1 overflow-auto min-h-64">
      <table className="w-full text-sm" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
        <thead>
          <tr>
            <th className="text-left font-medium py-2.5 pl-6 pr-4 whitespace-nowrap" style={{ color: "#222", background: "#F9F9FF", position: "sticky", top: 0, left: 0, zIndex: Z_INDEX.FIXED_COLUMN, width: CLIENT_COL_WIDTH, minWidth: CLIENT_COL_WIDTH, maxWidth: CLIENT_COL_WIDTH, borderRight: "1px solid #ECECEC", boxShadow: "inset 0 1px 0 #ECECEC, inset 0 -1px 0 #ECECEC" }}>{t('client')}</th>
            <th className="font-medium py-2.5 px-2 whitespace-nowrap" style={{ color: "#222", background: "#F9F9FF", position: "sticky", top: 0, left: CLIENT_COL_WIDTH, zIndex: Z_INDEX.FIXED_COLUMN, minWidth: ALL_COL_WIDTH, borderRight: "1px solid #ECECEC", boxShadow: "inset 0 1px 0 #ECECEC, inset 0 -1px 0 #ECECEC" }}>{t('all')}</th>
            {lobs.map((lob) => (
              <th key={lob.id} className="font-medium py-2.5 px-2 whitespace-nowrap" style={{ color: "#222", background: "#F9F9FF", position: "sticky", top: 0, minWidth: 90, boxShadow: "inset 0 1px 0 #ECECEC, inset 0 -1px 0 #ECECEC" }}>
                {lob.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* Pinned, non-virtualized "Unmapped clients (default)" row — always shown (it's a global
              rule, not subject to search/filters). */}
          {defaultClient && (
            <DefaultSyncSettingsRow
              client={defaultClient}
              lobs={lobs}
              enabledLobIds={draft[defaultClient.id] ?? []}
              allState={getAllCheckboxState(defaultClient.id)}
              tooltipContainer={tooltipContainer}
              onToggleLob={onToggleLob}
              onToggleAll={onToggleAll}
            />
          )}
          {filteredClients.length === 0 && (
            <tr><td colSpan={2 + lobs.length} className="py-16 text-center text-sm text-gray-400">{t('atsSyncNoClientsMatch')}</td></tr>
          )}
          {paddingTop > 0 && (
            <tr><td colSpan={2 + lobs.length} style={{ height: paddingTop, padding: 0, border: 0 }} /></tr>
          )}
          {virtualItems.map((virtualRow) => {
            const client = filteredClients[virtualRow.index]!
            return (
              <SyncSettingsRow
                key={client.id}
                client={client}
                lobs={lobs}
                enabledLobIds={draft[client.id]!}
                duplicateAtsClientIds={duplicateAtsClientIds}
                addressLine={getAddressLine(client)}
                isModified={changedClientIds.has(client.id)}
                tooltipContainer={tooltipContainer}
                orgDetailsLinkTemplate={orgDetailsLinkTemplate}
                onToggleLob={onToggleLob}
                onToggleAll={onToggleAll}
              />
            )
          })}
          {paddingBottom > 0 && (
            <tr><td colSpan={2 + lobs.length} style={{ height: paddingBottom, padding: 0, border: 0 }} /></tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

interface AtsSyncSettingsModalProps {
  isOpen: boolean
  onClose: () => void
  lobs: AtsLob[]
  filteredClients: AtsClientSyncSetting[]
  totalClientCount: number
  enabledCount: number
  onSearchTextChange: (value: string) => void
  filters: AtsSyncSettingsFilters
  duplicateAtsClientIds: Set<string>
  draft: Record<number, number[]>
  changedClientIds: Set<number>
  defaultClient: AtsClientSyncSetting | null
  getAllCheckboxState: (clientId: number) => true | false | "indeterminate"
  onToggleLob: (clientId: number, lobId: number) => void
  onToggleAll: (clientId: number) => void
  filterChips: string[]
  applyToAll: (lobIds: number[]) => void
  onSave: () => Promise<void>
  hasChanges: boolean
  isLoading: boolean
  isSaving: boolean
  error: string | null
  /**
   * FBO portal "ClientLink" action Link template from the WHC configuration,
   * e.g. "https://uat.flexbackoffice.com/next/organization/${Client.FboId}/details".
   * Used to open the org's portal page in a new tab. Optional — falls back to the
   * current window origin when absent.
   */
  orgDetailsLinkTemplate?: string
}

export function AtsSyncSettingsModal({
  isOpen,
  onClose,
  lobs,
  filteredClients,
  totalClientCount,
  enabledCount,
  onSearchTextChange,
  filters,
  duplicateAtsClientIds,
  draft,
  changedClientIds,
  defaultClient,
  getAllCheckboxState,
  filterChips,
  applyToAll,
  onToggleLob,
  onToggleAll,
  onSave,
  hasChanges,
  isLoading,
  isSaving,
  error,
  orgDetailsLinkTemplate,
}: AtsSyncSettingsModalProps) {
  const { t } = useTranslation()
  const [popoverContainer, setPopoverContainer] = useState<HTMLDivElement | null>(null)
  const [isApplyToAllOpen, setIsApplyToAllOpen] = useState(false)

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose() }}>
      <TooltipProvider delayDuration={300}>
      <DialogContent className="w-[90vw] max-w-6xl flex flex-col h-[80vh] p-0 gap-0">
        <div ref={setPopoverContainer} />
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-0 shrink-0">
          <DialogTitle className="text-base font-semibold text-blue-900">
            {t('atsSyncModalTitle')}
          </DialogTitle>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-sm text-gray-500">
              {t('atsSyncModalSubtitle')}
            </p>
            <span className="rounded-full bg-blue-100 text-blue-700 text-xs font-medium px-2.5 py-0.5 shrink-0">
              {t('atsSyncClientsEnabled', { count: String(enabledCount) })}
            </span>
          </div>
        </DialogHeader>

        {/* Search + filter bar */}
        <div className="flex items-center gap-3 px-6 py-4 shrink-0">
          <SyncSettingsSearchInput onSearchTextChange={onSearchTextChange} />
          <FiltersPopover filters={filters} portalContainer={popoverContainer} />
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="flex-1 flex items-center justify-center min-h-64">
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900" />
              <span className="text-gray-700 font-medium">{t('loading')}</span>
            </div>
          </div>
        ) : error ? (
          <div className="flex-1 flex items-center justify-center min-h-64 text-sm text-red-500">
            {error}
          </div>
        ) : (
          <SyncSettingsTable
            filteredClients={filteredClients}
            lobs={lobs}
            draft={draft}
            duplicateAtsClientIds={duplicateAtsClientIds}
            changedClientIds={changedClientIds}
            tooltipContainer={popoverContainer}
            defaultClient={defaultClient}
            getAllCheckboxState={getAllCheckboxState}
            getAddressLine={filters.formatClientAddress}
            orgDetailsLinkTemplate={orgDetailsLinkTemplate}
            onToggleLob={onToggleLob}
            onToggleAll={onToggleAll}
          />
        )}

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 shrink-0">
          <span className="text-sm text-gray-400">
            {filteredClients.length < totalClientCount
              ? t('atsSyncClientsShown', { shown: String(filteredClients.length), total: String(totalClientCount) })
              : t('atsSyncClientsTotal', { total: String(totalClientCount) })}
            {changedClientIds.size > 0 && (
              <span className="text-blue-500 ml-2">{t(changedClientIds.size === 1 ? 'atsSyncUnsavedChange' : 'atsSyncUnsavedChanges', { count: String(changedClientIds.size) })}</span>
            )}
          </span>
          <div className="flex gap-2">
            <Button
              variant="elliptical-outline"
              onClick={() => setIsApplyToAllOpen(true)}
              disabled={isLoading || isSaving || filteredClients.length === 0}
            >
              <Zap size={14} />
              {t('atsSyncApplyToAll', { count: String(filteredClients.length) })}
            </Button>
            <Button variant="elliptical-outline" onClick={onClose} disabled={isSaving}>
              {t('cancel')}
            </Button>
            <Button variant="elliptical" onClick={onSave} disabled={!hasChanges || isSaving || isLoading}>
              {isSaving ? t('atsSyncSaving') : t('save')}
            </Button>
          </div>
        </div>
      </DialogContent>
      </TooltipProvider>
      <ApplyToAllModal
        isOpen={isApplyToAllOpen}
        onClose={() => setIsApplyToAllOpen(false)}
        onApply={applyToAll}
        lobs={lobs}
        filteredCount={filteredClients.length}
        filterChips={filterChips}
      />
    </Dialog>
  )
}
